import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { fetchActionsActive, fetchActionsArchive } from '@/api/capa';
import type { ActionsQuickFilter } from '../-types';

interface UseActionsQueryParams {
  isArchiveView: boolean;
  quickFilter: ActionsQuickFilter;
  page: number;
  count: number;
  sort?: string;
  filters?: Record<string, string[]>;
  /**
   * Filters locked onto the list — embedded usages (e.g. the company-profile
   * CAPA tab) scope it with these. Merged last, so they win over any column
   * filter set for the same key.
   */
  defaultFilters?: Record<string, string>;
}

function buildQs(params: UseActionsQueryParams): string {
  const qs = new URLSearchParams();
  qs.set('page', String(Math.max(0, params.page - 1)));
  qs.set('count', String(params.count));
  if (params.sort) qs.set('sort', params.sort);
  if (params.quickFilter && params.quickFilter !== 'all') {
    qs.set('quickFilter', params.quickFilter);
  }
  const flat: Record<string, string> = {};
  if (params.filters) {
    for (const [key, vals] of Object.entries(params.filters)) {
      if (vals.length === 0) continue;
      if (key === 'collaboratorCompanyId') continue; // client-side only
      if (key === 'createdAt' || key === 'dueDate') {
        const [from, to] = vals;
        if (from) flat[`${key}From`] = from;
        if (to) flat[`${key}To`] = to;
        continue;
      }
      flat[key] = key === 'tags' ? vals.join(',') : vals[0]!;
    }
  }
  // Locked filters last: an embedded list's scope must not be overridable by a
  // column filter for the same key.
  Object.assign(flat, params.defaultFilters);
  if (Object.keys(flat).length > 0) qs.set('filter', JSON.stringify(flat));
  return qs.toString();
}

export function actionsQueryOptions(params: UseActionsQueryParams) {
  const qs = buildQs(params);
  return {
    queryKey: [
      'capa-actions',
      {
        isArchiveView: params.isArchiveView,
        quickFilter: params.quickFilter,
        page: params.page,
        count: params.count,
        sort: params.sort,
        filters: params.filters,
        // Part of the key so a scoped embed never shares a cache entry with the
        // global actions list.
        defaultFilters: params.defaultFilters,
      },
    ] as const,
    queryFn: () =>
      params.isArchiveView ? fetchActionsArchive(qs) : fetchActionsActive(qs),
  };
}

export function useActionsQuery(params: UseActionsQueryParams) {
  return useQuery({
    ...actionsQueryOptions(params),
    placeholderData: keepPreviousData,
  });
}
